/**
 * MechaGuard - LeaderboardSystem (本地排行榜与计分)
 *
 * 不联网，成绩存在 localStorage 里，按关卡各留前 10 名。
 * 服务端排行榜属于后续扩展（需要后端），这里先把「计分规则 + 本地纪录」
 * 这套地基打好，将来换成远程 API 只需替换 submit / getEntries。
 *
 * 计分公式（刻意做得可解释，玩家能看懂每个来源值多少分）
 *
 *   基础分 = 守住波次 × 100
 *          + 击杀数   × 5
 *          + 剩余生命 × 200
 *          + 剩余护盾 × 100
 *          + 通关奖励 3000（仅胜利）
 *          + 速度奖励 max(0, 1800 - 用时秒数) × 2（仅胜利，30 分钟封顶）
 *
 *   最终分 = round(基础分 × 难度系数)
 *   难度系数：前哨站 ×1.00 / 峡谷通道 ×1.25 / 核心反应堆 ×1.55
 *
 * 先按分数降序；同分则用时短的排前面。
 */

import { readJson, writeJson } from './Storage.js';

const STORAGE_KEY = 'mechguard.leaderboard.v1';
const MAX_ENTRIES = 10;

/** 速度奖励的基准用时（秒）：超过这个时长就没有速度分了 */
const SPEED_BONUS_BASE = 1800;
/** 每快 1 秒给多少分 */
const SPEED_BONUS_PER_SEC = 2;
/** 通关基础奖励 */
const VICTORY_BONUS = 3000;

/** 关卡难度 -> 计分系数 */
const DIFFICULTY_MULTIPLIER = { 1: 1.0, 2: 1.25, 3: 1.55 };

/**
 * 计算得分
 * @param {object} s
 * @param {number} s.wavesCleared
 * @param {number} s.kills
 * @param {number} s.lives
 * @param {number} [s.shield]
 * @param {boolean} s.victory
 * @param {number} s.elapsed 秒
 * @param {number} s.difficulty 1~3
 * @returns {number}
 */
export function computeScore(s) {
  const stats = s || {};

  const wavesCleared = Math.max(0, Number(stats.wavesCleared) || 0);
  const kills = Math.max(0, Number(stats.kills) || 0);
  const lives = Math.max(0, Number(stats.lives) || 0);
  const shield = Math.max(0, Number(stats.shield) || 0);
  const elapsed = Math.max(0, Number(stats.elapsed) || 0);
  const victory = !!stats.victory;
  const difficulty = Number(stats.difficulty) || 1;

  let base =
    wavesCleared * 100 +
    kills * 5 +
    lives * 200 +
    shield * 100;

  if (victory) {
    base += VICTORY_BONUS;
    base += Math.max(0, SPEED_BONUS_BASE - elapsed) * SPEED_BONUS_PER_SEC;
  }

  const mul = DIFFICULTY_MULTIPLIER[difficulty] || 1.0;
  return Math.round(base * mul);
}

/** 计分规则说明（给 UI 展示用） */
export const SCORE_RULES = Object.freeze([
  { label: '守住波次', value: '× 100' },
  { label: '击杀数', value: '× 5' },
  { label: '剩余生命', value: '× 200' },
  { label: '剩余护盾', value: '× 100' },
  { label: '通关奖励', value: `+ ${VICTORY_BONUS}` },
  { label: '速度奖励', value: `每快 1 秒 +${SPEED_BONUS_PER_SEC}（30 分钟封顶）` },
  { label: '难度系数', value: '× 1.00 / 1.25 / 1.55' },
]);

/** 排序：分数降序 -> 用时升序 -> 时间早的优先 */
function compareEntries(a, b) {
  if (b.score !== a.score) return b.score - a.score;
  if (a.elapsed !== b.elapsed) return a.elapsed - b.elapsed;
  return a.date - b.date;
}

function toInt(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : fallback;
}

export class LeaderboardSystem {
  /**
   * @param {object} [opts]
   * @param {{getItem:Function,setItem:Function}|null} [opts.storage]
   */
  constructor({ storage = null } = {}) {
    this.storage = storage;

    const loaded = readJson(storage, STORAGE_KEY, null);
    const levels = (loaded && typeof loaded === 'object' && loaded.levels) ? loaded.levels : {};

    /** @type {Record<string, object[]>} */
    this._levels = {};

    for (const levelId of Object.keys(levels)) {
      const normalized = this._normalize(levels[levelId]);
      if (normalized.length > 0) this._levels[levelId] = normalized;
    }
  }

  /* ===================================================
   * 提交成绩
   * =================================================== */

  /**
   * @param {object} stats
   * @param {string} stats.levelId
   * @param {string} [stats.levelName]
   * @param {number} stats.wavesCleared
   * @param {number} stats.totalWaves
   * @param {number} stats.kills
   * @param {number} stats.lives
   * @param {number} [stats.shield]
   * @param {number} [stats.towers]
   * @param {number} stats.elapsed
   * @param {number} stats.difficulty
   * @param {boolean} stats.victory
   * @returns {{entry:object, rank:number|null, isBest:boolean, onBoard:boolean}}
   */
  submit(stats) {
    const s = stats || {};

    const entry = {
      score: computeScore(s),
      victory: !!s.victory,
      wavesCleared: toInt(s.wavesCleared),
      totalWaves: toInt(s.totalWaves),
      kills: toInt(s.kills),
      lives: toInt(s.lives),
      towers: toInt(s.towers),
      elapsed: toInt(s.elapsed),
      difficulty: toInt(s.difficulty, 1),
      date: Date.now(),
    };

    const levelId = (typeof s.levelId === 'string' && s.levelId) ? s.levelId : 'unknown';

    const list = this.getEntries(levelId);
    list.push(entry);
    list.sort(compareEntries);

    const trimmed = list.slice(0, MAX_ENTRIES);
    this._levels[levelId] = trimmed;
    this._save();

    const index = trimmed.indexOf(entry);

    return {
      entry,
      rank: index >= 0 ? index + 1 : null,
      isBest: index === 0,
      onBoard: index >= 0,
    };
  }

  /* ===================================================
   * 查询
   * =================================================== */

  /** 某关卡的前 N 名（默认全部，已排序） */
  getEntries(levelId, limit = MAX_ENTRIES) {
    const list = this._levels[levelId];
    if (!Array.isArray(list)) return [];
    const copy = list.slice().sort(compareEntries);
    return limit > 0 ? copy.slice(0, limit) : copy;
  }

  /** 某关卡的最高分记录 */
  getBest(levelId) {
    const list = this.getEntries(levelId, 1);
    return list.length > 0 ? list[0] : null;
  }

  /** 是否有任何记录 */
  get isEmpty() {
    return Object.keys(this._levels).length === 0;
  }

  /** 有记录的关卡数 */
  get levelCount() {
    return Object.keys(this._levels).length;
  }

  /** 全部关卡的记录条数合计 */
  get entryCount() {
    return Object.keys(this._levels)
      .reduce((n, id) => n + this._levels[id].length, 0);
  }

  /* ===================================================
   * 维护
   * =================================================== */

  /** 清空排行榜。传 levelId 只清那一关 */
  clear(levelId = null) {
    if (levelId) delete this._levels[levelId];
    else this._levels = {};
    this._save();
  }

  _save() {
    writeJson(this.storage, STORAGE_KEY, { levels: this._levels });
  }

  /** 把外部读到的（可能被改坏的）数组规范化 */
  _normalize(raw) {
    if (!Array.isArray(raw)) return [];

    return raw
      .filter(e => e && typeof e === 'object' && Number.isFinite(Number(e.score)))
      .map(e => ({
        score: toInt(e.score),
        victory: !!e.victory,
        wavesCleared: toInt(e.wavesCleared),
        totalWaves: toInt(e.totalWaves),
        kills: toInt(e.kills),
        lives: toInt(e.lives),
        towers: toInt(e.towers),
        elapsed: toInt(e.elapsed),
        difficulty: toInt(e.difficulty, 1),
        date: toInt(e.date),
      }))
      .sort(compareEntries)
      .slice(0, MAX_ENTRIES);
  }
}
